/**
 * ExpandableCallout — shared types, Zod prop schema, and styling tokens.
 *
 * A persistent informational callout whose header (leading info icon + title +
 * trailing chevron) toggles an inline disclosed panel *in place* — the
 * surrounding layout shifts down, no scrim (distinct from DisclosureSection's
 * floating scrim panel). Owns its own open/close state. The disclosed content
 * is composed from catalog children, so it stays presentational and
 * domain-agnostic (e.g. a unit-equivalence row or an explanatory paragraph).
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { typography } from '../../elements/typography';
import { duration } from '../../elements/animations';
import type { IconName } from '../../elements/icons/registry';

/** Catalog-facing schema (plain ZodObject so `formatZodType` can introspect it). */
export const expandableCalloutPropsSchema = z.object({
  title: z.string(),
  /** Whether the panel is disclosed on first render. */
  defaultExpanded: z.boolean().default(false),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
});

export type ExpandableCalloutProps = z.infer<typeof expandableCalloutPropsSchema>;

export const EXPANDABLE_CALLOUT_RADIUS = radius.xl;
export const EXPANDABLE_CALLOUT_PADDING = spacing.m;
/** Gap between the leading icon, the title, and the trailing chevron. */
export const EXPANDABLE_CALLOUT_HEADER_GAP = spacing.s;
/** Gap between the header and the disclosed panel. */
export const EXPANDABLE_CALLOUT_PANEL_GAP = spacing.lg;

// Figma `Colours/cerulean/200` — a lighter blue than the shared `background.callout`
// token (#deebfe), which is consumed by ~10 other components; a dedicated
// palette entry keeps this callout on-spec without shifting them.
export const EXPANDABLE_CALLOUT_BACKGROUND = colors.cerulean['200'];
export const EXPANDABLE_CALLOUT_TEXT_COLOR = colors.text.primary;
export const EXPANDABLE_CALLOUT_ICON_COLOR = colors.text.primary;
// Figma pins the header info/chevron glyphs at 18px, between the sizing
// scale's xs (16) and sm (24) — no exact token, so (matching the
// TEXT_INPUT_ERROR_ICON_SIZE precedent) this is a component-specific literal.
export const EXPANDABLE_CALLOUT_ICON_SIZE = 18;

/** Expands the header's tappable area to the minimum touch target without
 * adding visible height (the header row is only icon/line-box tall). */
export const EXPANDABLE_CALLOUT_HIT_SLOP =
  (sizing.touchTarget.minimum - EXPANDABLE_CALLOUT_ICON_SIZE) / 2;
/** Minimum tappable height for the web disclosure button. */
export const EXPANDABLE_CALLOUT_MIN_TOUCH_TARGET = sizing.touchTarget.minimum;

export const EXPANDABLE_CALLOUT_LEADING_ICON: IconName = 'info';
export const EXPANDABLE_CALLOUT_CHEVRON_EXPANDED: IconName = 'up-chevron';
export const EXPANDABLE_CALLOUT_CHEVRON_COLLAPSED: IconName = 'down-chevron';

export const EXPANDABLE_CALLOUT_TITLE_TYPOGRAPHY = typography.b2Bold;

// Reveal animation duration (ms) for the disclose/collapse height + opacity
// transition. Uses the shared `duration.reveal` token (ADR-012).
export const EXPANDABLE_CALLOUT_REVEAL_DURATION = duration.reveal;

/** Accessibility label falls back to the visible title. */
export function expandableCalloutAccessibleLabel(
  title: string,
  accessibilityLabel?: string,
): string {
  return accessibilityLabel ?? title;
}
